/**
 * supabase.ts — the app's data connection.
 *
 * PRIMARY (2026-08-14): the app talks to OUR OWN backend. `supabase.from(...)`
 * is a thin shim over dataClient, which calls /api/v1/data/{table} on FastAPI;
 * that runs the query under Postgres RLS with the caller's native JWT. The
 * supabase-js library is NOT used for data anymore.
 *
 * What remains on this shim (and why):
 *   - `auth.*`       : legacy bridge. Our OWN native auth (authNative) is the
 *                      primary login; this keeps the shape for any code that
 *                      still calls supabase.auth.getSession etc. It resolves
 *                      against our seeded native session when configured.
 *   - `storage.from` : file uploads route through our backend/MinIO first
 *                      (see src/lib/storage.ts + uploads.ts); this is the
 *                      fallback path for legacy/cousin deployments.
 *   - `functions.invoke` : legacy edge-function bridge.
 *
 * `.from()` and `.rpc()` are ALWAYS our data layer (dataClient) — unconditional,
 * with no dependence on REACT_APP_API_BASE_URL. No Supabase call is ever made
 * for `.from()`/`.rpc()`; dataClient talks to /api/v1/data on our backend. The
 * `.auth`/`.storage` bridges below do NOT reach Supabase either — they return an
 * unavailability error (see `functions.invoke` for the shape) rather than a
 * silent fake success.
 */

import { dataClient } from './dataClient';
import type { AuthChangeEvent, Session, User } from '@supabase/supabase-js';

// ⛔ NO LIVE SUPABASE ADDRESS IN THIS FILE (Lis, 2026-10-19, item 11). It used to carry the
// production project's URL and anon key "for any code that referenced them" - nothing did,
// and on `main` the same pair is the fallback that sends jest to production when no env is
// set. A fallback that is never meant to run is a fallback somebody eventually re-wires.

// Mirror of apiClient's getApiBase() gate, read here WITHOUT importing
// apiClient (apiClient imports supabase — a cycle). Matches apiConfigured().
const selfHosted = !!(process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

/**
 * The drop-in `supabase` object. `.from()`/`.rpc()` are ALWAYS our data layer
 * (never Supabase); `.auth`/`.storage`/`.functions` are the legacy bridges.
 */
export const supabase = {
  from: dataClient.from,
  rpc: dataClient.rpc,
  // ── Legacy bridges ──────────────────────────────────────────────────────
  auth: legacyAuth(),
  storage: legacyStorage(),
  functions: { invoke: functionsInvoke() },
  // Legacy realtime channel stub (the fallback path for deployed/cousin
  // deployments that still use Supabase realtime; our self-hosted mode uses
  // subscribeRoom / subscribePresenceRoom instead).
  channel: (..._args: unknown[]): RealtimeChannelStub => {
    const ch: RealtimeChannelStub = {
      on: () => ch,
      subscribe: () => ch,
      unsubscribe: () => {},
      send: () => {},
      track: () => Promise.resolve(),
      untrack: () => Promise.resolve(),
      presenceState: () => ({} as Record<string, unknown>),
    };
    return ch;
  },
  removeChannel: (..._args: unknown[]) => {},
};

// ── Legacy implementations ────────────────────────────────────────────────────

/**
 * Minimal typed shape for the legacy Supabase realtime channel, matching the
 * methods the fallback branches call (.on('postgres_changes'|'presence'|
 * 'broadcast', opts, cb) .subscribe(cb) .track(...) .send(...) .untrack()).
 * Used only by the deployed/cousin fallback paths; self-hosted mode uses
 * subscribeRoom / subscribePresenceRoom instead.
 */
interface RealtimeChannelStub {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy stub mirroring supabase-js's realtime API
  on(...args: any[]): RealtimeChannelStub;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy stub
  subscribe(...args: any[]): RealtimeChannelStub;
  unsubscribe(): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy stub
  send(...args: any[]): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy stub
  track(...args: any[]): Promise<unknown>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy stub
  untrack(...args: any[]): Promise<unknown>;
  presenceState(): Record<string, unknown>;
}

function legacyAuth() {
  // Native auth is primary; these methods exist for code that still calls
  // supabase.auth.*. getSession/getUser return OUR native session when
  // self-hosted (it's the real session), so `.data.session.access_token` and
  // `.data.user.id` reads keep working.
  const native = () => {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('pulsehq_native_session') : null;
      return raw ? JSON.parse(raw) as { access_token?: string; user?: { id?: string; email?: string | null } } : null;
    } catch {
      return null;
    }
  };
  return {
    getSession: async (): Promise<{ data: { session: Session | null }; error: { message: string } | null }> => {
      const n = native();
      return n?.access_token
        ? { data: { session: { access_token: n.access_token, refresh_token: '', user: { id: n.user?.id ?? '', email: n.user?.email ?? undefined } } as Session }, error: null }
        : { data: { session: null }, error: null as { message: string } | null };
    },
    getUser: async (): Promise<{ data: { user: User | null }; error: { message: string } | null }> => {
      const n = native();
      return n?.user?.id
        ? { data: { user: { id: n.user.id, email: n.user.email ?? undefined } as User }, error: null }
        : { data: { user: null }, error: null as { message: string } | null };
    },
    setSession: async (..._args: unknown[]) => ({ data: { session: null }, error: null }),
    signOut: async () => ({ error: null }),
    // These do NO real work — sign-in is native auth (src/lib/authNative.ts),
    // which AuthContext.signIn uses whenever a backend is configured. Reaching
    // one of these means no backend is configured; say so rather than reporting
    // a sign-in that never happened. Shape copied from functions.invoke below.
    signInWithPassword: async (..._a: unknown[]): Promise<{ data: unknown; error: { message: string } | null }> => ({
      data: null,
      error: { message: 'supabase.auth.signInWithPassword is unavailable: sign-in uses native auth (src/lib/authNative.ts), not this legacy shim.' },
    }),
    signInWithOtp: async (..._a: unknown[]): Promise<{ error: { message: string } | null }> => ({
      error: { message: 'supabase.auth.signInWithOtp is unavailable: no Supabase backend is configured.' },
    }),
    signInWithOAuth: async (..._a: unknown[]): Promise<{ data: { provider: string }; error: { message: string } | null }> => ({
      data: { provider: '' },
      error: { message: 'supabase.auth.signInWithOAuth is unavailable: no Supabase backend is configured.' },
    }),
    /**
     * Password change (SettingsPage / ProfilePage). Self-hosted: calls our
     * /auth/password endpoint with the native token. Legacy: no-op (the
     * deployed/cousin path uses Supabase's own updateUser).
     */
    updateUser: async (attrs: { password?: string; data?: Record<string, unknown> }) => {
      if (attrs?.password && selfHosted) {
        try {
          const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('pulsehq_native_session') : null;
          const parsed = raw ? JSON.parse(raw) as { access_token?: string } : null;
          const token = parsed?.access_token;
          if (!token) return { data: { user: null }, error: { message: 'Not signed in' } };
          const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
          // The caller (SettingsPage) sends only the new password; we need the
          // current one too, so surface that it's required rather than guess.
          if (attrs.password && !(attrs as { current_password?: string }).current_password) {
            // Supabase's shape was {password}; ours needs current+new. The
            // pages only send new — so patch them to send current as well by
            // reading the page's own state is not possible here. Return an
            // instructive error the UI already shows.
            return {
              data: { user: null },
              error: { message: 'Password change now requires your current password' },
            };
          }
          const res = await fetch(`${base}/auth/password`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({
              current_password: (attrs as { current_password?: string }).current_password,
              new_password: attrs.password,
            }),
          });
          if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            return {
              data: { user: null },
              error: { message: (body as { detail?: string }).detail || `HTTP ${res.status}` },
            };
          }
          return { data: { user: null }, error: null };
        } catch (err) {
          return { data: { user: null }, error: { message: err instanceof Error ? err.message : 'Failed' } };
        }
      }
      return { data: { user: null }, error: null };
    },
    // supabase-js's own signature, so a listener written against it gets typed
    // parameters. `(..._a: unknown[])` left them untyped, and ThemeContext's
    // `(_event, session) => …` failed CRA's build with TS7006 (2026-10-18).
    // The shim never calls it: our own sign-in does not emit auth events.
    onAuthStateChange: (
      _callback: (event: AuthChangeEvent, session: Session | null) => void,
    ) => ({ data: { subscription: { unsubscribe: () => {} } } }),
  };
}

function legacyStorage() {
  // This shim does NO real storage work. The real path is src/lib/storage.ts
  // (MinIO via our backend). Every method here returns an unavailability error
  // — same shape as functions.invoke below — so anything that reaches it fails
  // loudly instead of reporting a silent fake success and losing the file.
  const obj: {
    upload: (..._a: unknown[]) => Promise<{ data: unknown; error: { message: string } | null }>;
    getPublicUrl: (..._a: unknown[]) => { data: { publicUrl: string } };
    createSignedUrl: (..._a: unknown[]) => Promise<{ data: { signedUrl: string } | null; error: { message: string } | null }>;
    // Batch form, added when main's signPaths() started calling it. Same shape
    // supabase-js returns: one row per path, each with its own signed URL.
    createSignedUrls: (..._a: unknown[]) => Promise<{
      data: { path: string; signedUrl: string }[] | null;
      error: { message: string } | null;
    }>;
    remove: (..._a: unknown[]) => Promise<{ data: unknown; error: { message: string } | null }>;
  } = {
    upload: async () => ({
      data: null,
      error: { message: 'supabase.storage.upload is unavailable: file storage goes through the backend (src/lib/storage.ts / uploads.ts), not this legacy shim.' },
    }),
    // getPublicUrl is synchronous and has no error channel in supabase-js's
    // signature, and no caller in this app — so the honest "unavailable" signal
    // is to throw, not to hand back a silent empty URL.
    getPublicUrl: () => {
      throw new Error('supabase.storage.getPublicUrl is unavailable: file URLs are minted by the backend (src/lib/storage.ts), not this legacy shim.');
    },
    createSignedUrl: async () => ({
      data: null,
      error: { message: 'supabase.storage.createSignedUrl is unavailable: signed URLs are minted by the backend (src/lib/storage.ts), not this legacy shim.' },
    }),
    createSignedUrls: async () => ({
      data: null,
      error: { message: 'supabase.storage.createSignedUrls is unavailable: signed URLs are minted by the backend (src/lib/storage.ts), not this legacy shim.' },
    }),
    remove: async () => ({
      data: null,
      error: { message: 'supabase.storage.remove is unavailable: file deletion goes through the backend (src/lib/storage.ts), not this legacy shim.' },
    }),
  };
  return {
    from: (..._bucket: unknown[]) => obj,
  };
}

/**
 * `supabase.functions.invoke(name, { body })` — server-side work.
 *
 * This used to return `{ data: null, error: null }` for everything: a SUCCESS.
 * So PulseBot's AI, sending a campaign, charging an invoice and integration
 * sync all reported that they had worked while doing nothing whatsoever. That
 * is the exact shape of failure the app's "never fail silently" law exists to
 * prevent, and it is invisible to both the type checker and the test suite.
 *
 * Self-hosted, it now posts to our own backend at `/functions/{name}`. Anything
 * not yet ported off Supabase answers 501 there, which arrives here as a REAL
 * error carrying a message the UI can show. Unavailable and working no longer
 * look the same to the caller.
 */
function functionsInvoke() {
  return async (
    name: string,
    opts?: { body?: unknown },
    // `data` is permissively typed for the same reason dataClient's is: callers
    // read `data.url` / `data.error` off it, and supabase-js typed it as `any`.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors supabase-js
  ): Promise<{ data: any; error: { message: string } | null }> => {
    if (!selfHosted) {
      // Legacy/cousin deployment — the real Supabase functions gateway is not
      // reachable from this shim, so say so rather than reporting success.
      return {
        data: null,
        error: { message: `'${name}' is unavailable: no backend is configured.` },
      };
    }
    try {
      const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      const raw = typeof localStorage !== 'undefined'
        ? localStorage.getItem('pulsehq_native_session')
        : null;
      const token = raw ? (JSON.parse(raw) as { access_token?: string }).access_token : undefined;

      const res = await fetch(`${base}/functions/${encodeURIComponent(name)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(opts?.body ?? {}),
      });

      const text = await res.text();
      const parsed = text ? JSON.parse(text) : null;
      if (!res.ok) {
        const detail =
          (parsed as { detail?: string } | null)?.detail || `HTTP ${res.status}`;
        return { data: null, error: { message: detail } };
      }
      return { data: parsed, error: null };
    } catch (err) {
      return {
        data: null,
        error: { message: err instanceof Error ? err.message : `'${name}' failed` },
      };
    }
  };
}

// Visible breadcrumb.
if (selfHosted) {
  // eslint-disable-next-line no-console
  console.info('[supabase] Data layer: OUR backend (self-hosted Postgres)');
} else {
  // eslint-disable-next-line no-console
  console.info('[supabase] Data layer: legacy Supabase fallback');
}
